import { useState } from 'react'
import { CreditCard, Smartphone } from 'lucide-react'
import { Dialog, DialogContent } from '@/components/ui/dialog'
import type { Gateways } from '@/types/apply'

interface PaymentModalProps {
  gateways: Gateways
  onClose: () => void
  onSubmit: (gateway: string) => void
  loading: boolean
  // 'mandate' re-labels the gateway buttons for the no-charge mandate-setup flow
  // (the verification fee is already paid). Defaults to the original pay flow.
  variant?: 'payment' | 'mandate'
}

export default function PaymentModal({ gateways, onClose, onSubmit, loading, variant = 'payment' }: PaymentModalProps) {
  const [selected, setSelected] = useState<string | null>(null)
  const mandate = variant === 'mandate'

  // Fake mode: skip modal entirely
  if (gateways.fake && !gateways.stripe && !gateways.razorpay) {
    // Auto-submit with fake gateway
    onSubmit('fake')
    return null
  }

  const availableGateways = [
    gateways.stripe && { key: 'stripe', label: mandate ? 'Continue with Card' : 'Pay with Card', sublabel: 'Credit or Debit Card', icon: CreditCard },
    gateways.razorpay && { key: 'razorpay', label: mandate ? 'Continue with UPI / Netbanking' : 'Pay with UPI / Netbanking', sublabel: 'UPI, Netbanking, Wallet', icon: Smartphone }
  ].filter(Boolean) as { key: string; label: string; sublabel: string; icon: typeof CreditCard }[]

  // If only one gateway, auto-select it
  if (availableGateways.length === 1 && !selected) {
    setSelected(availableGateways[0].key)
  }

  return (
    <Dialog open onOpenChange={(next) => { if (!next) onClose() }}>
      <DialogContent title="Choose Payment Method" size="sm">
        <div className="mt-2 space-y-3">
          {availableGateways.map(gw => (
            <button
              key={gw.key}
              type="button"
              onClick={() => setSelected(gw.key)}
              disabled={loading}
              className={`apply-card-selectable w-full ${selected === gw.key ? 'is-selected' : ''}`}
            >
              <div className="flex items-center gap-4">
                <gw.icon className="h-6 w-6 flex-shrink-0" />
                <div className="text-left">
                  <div className="font-semibold">{gw.label}</div>
                  <div className="text-xs text-white/40">{gw.sublabel}</div>
                </div>
              </div>
            </button>
          ))}
        </div>

        <button
          type="button"
          onClick={() => selected && onSubmit(selected)}
          disabled={!selected || loading}
          className="marketing-button-primary mt-6 w-full"
        >
          {loading ? (
            <span className="flex items-center justify-center gap-2">
              <svg className="h-4 w-4 animate-spin" viewBox="0 0 24 24" fill="none">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
              </svg>
              Processing...
            </span>
          ) : (
            'Pay & Submit'
          )}
        </button>
      </DialogContent>
    </Dialog>
  )
}
